import React, { use, useEffect, useState } from 'react'

import { AnimatePresence, motion } from 'framer-motion'
import { Link, useNavigate } from 'react-router-dom'
import {
  Button,
  chakra,
  Heading,
  HStack,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useStripe } from '@stripe/react-stripe-js'

import { LookupKey } from '@pkg/types/prices'

import { AuthContext } from '@/context/AuthContext'
import { HeaderContext } from '@/context/Header'
import { usePrices } from '@/react-query/prices'
import { useCreateCheckoutSession } from '@/react-query/stripe'
import { useTeam } from '@/react-query/team'
import { useUser } from '@/react-query/user'
import ArrowLeftLong from '@/views/assets/ArrowLeftLong'

const PricingPage: React.FC = () => {
  const { setMiddleContent } = use(HeaderContext)
  const authContext = use(AuthContext)

  const prices = usePrices()

  const navigate = useNavigate()
  const user = useUser()

  useEffect(() => {
    if (user.data && user.data.plan !== 'FREE') {
      navigate('/home')
    }
  }, [user.data?.plan, navigate])

  useEffect(() => {
    setMiddleContent(
      <Button
        as={Link}
        className={'ml-6 rounded-full'}
        to={'/home'}
        variant={'brand-solid-white'}
        rounded={'full'}
        px={'1.15rem'}
        display={{
          base: 'none',
          sm: 'flex',
        }}
      >
        <ArrowLeftLong /> &nbsp;&nbsp;&nbsp;Home
      </Button>,
    )
  }, [])

  const [billingCycle, setBillingCycle] = useState<'MONTHLY' | 'YEARLY'>(
    'MONTHLY',
  )

  const toggleBillingCycle = (cycle: 'MONTHLY' | 'YEARLY') => {
    setBillingCycle(cycle)
  }

  if (prices.isLoading || !prices.data) {
    return null
  }

  return (
    <AnimatePresence>
      <VStack spacing={8} align="center" mt={8}>
        <HStack align={'center'} spacing={4}>
          <Button
            variant={
              billingCycle === 'MONTHLY' ? 'brand-blue' : 'brand-solid-gray'
            }
            onClick={() => toggleBillingCycle('MONTHLY')}
            px={8}
            w={'120px'}
          >
            Monthly
          </Button>
          <Button
            variant={
              billingCycle === 'YEARLY' ? 'brand-blue' : 'brand-solid-gray'
            }
            px={8}
            w={'120px'}
            onClick={() => toggleBillingCycle('YEARLY')}
          >
            Yearly
          </Button>
        </HStack>
        <Stack
          display="flex"
          justifyContent="center"
          align={'center'}
          width="100%"
          mt={6}
          spacing={8}
          direction={{ base: 'column', md: 'row' }}
        >
          <AnimatePresence mode={'wait'}>
            <PricingPlan
              key={`${billingCycle}-PRO`}
              name="Pro"
              description="Up to 40 interview transcriptions per month."
              price={
                billingCycle === 'MONTHLY'
                  ? prices.data.pro_plan_monthly_price
                  : prices.data.pro_plan_yearly_price
              }
              period={billingCycle === 'MONTHLY' ? 'month' : 'year'}
              lookupKey={
                billingCycle === 'MONTHLY'
                  ? 'pro_plan_monthly_price'
                  : 'pro_plan_yearly_price'
              }
            />
            <PricingPlan
              key={`${billingCycle}-BUSINESS`}
              name="Business"
              description="Unlimited interview transcriptions and reports."
              price={
                billingCycle === 'MONTHLY'
                  ? prices.data.business_plan_monthly_price
                  : prices.data.business_plan_yearly_price
              }
              period={billingCycle === 'MONTHLY' ? 'month' : 'year'}
              animationDelay={0.1}
              lookupKey={
                billingCycle === 'MONTHLY'
                  ? 'business_plan_monthly_price'
                  : 'business_plan_yearly_price'
              }
            />
          </AnimatePresence>
        </Stack>
      </VStack>
    </AnimatePresence>
  )
}

const PricingPlan = ({
  name,
  description,
  price,
  period,
  animationDelay,
  lookupKey,
}: {
  name: string
  description: string
  price: number
  period: 'month' | 'year'
  animationDelay?: number
  lookupKey: LookupKey
}) => {
  const createCheckoutSession = useCreateCheckoutSession()
  const stripe = useStripe()
  const team = useTeam()

  if (!stripe) {
    return null
  }

  const handleSubscribe = async () => {
    const res = await createCheckoutSession.mutateAsync({ lookupKey })
    // use res.sessionId to start stripe checkout

    await stripe.redirectToCheckout({
      sessionId: res.sessionId,
    })
  }

  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{
        duration: 0.2,
        ease: 'easeInOut',
        delay: animationDelay || 0,
      }}
      rounded={'2.5rem'}
      bg={'#F5F5F5'}
      p={10}
      maxW={'300px'}
    >
      <VStack spacing={6} align="stretch">
        <Heading size="lg">{name}</Heading>
        <Text color={'gray.500'}>{description}</Text>
        <HStack>
          <Text fontSize="4xl" fontWeight="bold" justifyContent={'flex-start'}>
            €{price}
          </Text>
          <Text
            lineHeight={4}
            color={'gray.500'}
            fontSize={'sm'}
            ml={-1}
            textAlign={'left'}
          >
            per
            <br />
            {period}
          </Text>
          {team.data && (
            <Text
              lineHeight={4}
              color={'gray.500'}
              fontSize={'sm'}
              textAlign={'left'}
            >
              per
              <br />
              seat
            </Text>
          )}
        </HStack>
        <Button
          variant={'brand-blue'}
          onClick={handleSubscribe}
          isLoading={createCheckoutSession.isPending}
        >
          Subscribe
        </Button>
      </VStack>
    </MotionBox>
  )
}

const MotionBox = motion(chakra.div)

export default PricingPage
